import {
  Avatar,
  Badge,
  Checkbox,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  ModalBody,
  ModalFooter,
  Select,
  SimpleGrid,
  Switch,
  Text,
  Textarea,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Plus, Trash2, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import {
  trpcAdmin,
  type RouterInputs,
  type RouterOutputs,
} from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { RequirementChip } from '../../../components/requirements/RequirementChip';
import { ConfirmationDialog } from '@duke-hq/ui/components/DialogBox/ConfirmationDialog';
import { iconSize, pxToRem } from '@duke-hq/ui/constants/scalable';

import {
  type CastPart,
  RECURRING_ROLES,
  type RequirementRef,
} from '@duke-hq/unit-generation-pipeline/schemas/sectionPlan';

import { testIds } from './testIds';
import {
  BlockersAlert,
  FieldLabel,
  GroupHeading,
  OutlineHeading,
  OutlineItem,
  PendingBody,
  ReviewLayout,
  ReviewShell,
} from './ReviewLayout';
import { RequirementPicker } from '../../../components/requirements/RequirementPicker';
import { FoundationSkillPicker } from '../../../components/requirements/FoundationSkillPicker';
import {
  KIND_COLOURS,
  requirementId,
  requirementLabel,
  UnitRequirementKindHeading,
} from '../../../components/requirements/kinds';
import { hasFixedWorkplace } from '@duke-hq/unit-generation-pipeline/generate/prompts/learnerProfile';

import { blank } from '../reviewedRequirements';

type SectionPlanData = RouterOutputs['unitsOfCompetency']['sectionPlan'];
type SaveInput = RouterInputs['unitsOfCompetency']['saveAndContinueFromAllocation'];
type CastRef = SaveInput['sections'][number]['cast'][number];
type DraftSection = SectionPlanData['sections'][number];

type Draft = Pick<SectionPlanData, 'sections' | 'recurringCast' | 'learnerProfile'>;

type PaneSelection = { kind: 'castAndContext' } | { kind: 'section'; key: string };

const LEARNER_PROFILE_FIELDS = [
  { key: 'jobRole', label: 'Job role', long: false, required: false },
  { key: 'workplace', label: 'Workplace', long: false, required: true },
  { key: 'location', label: 'Location', long: false, required: true },
  { key: 'organisationalContext', label: 'Organisational context', long: true, required: false },
] as const;

function missingWorkplaceFields(learnerProfile: Draft['learnerProfile']) {
  return LEARNER_PROFILE_FIELDS.filter(({ key, required }) => {
    const value = learnerProfile[key];

    return required && (typeof value !== 'string' || blank(value));
  }).map(({ label }) => label);
}

function withoutBlankUsage(member: CastRef): CastRef {
  return {
    personaId: member.personaId,
    name: member.name,
    ...(member.usage == null || blank(member.usage) ? {} : { usage: member.usage }),
    ...(member.part == null ? {} : { part: member.part }),
  };
}

function submittableMember(member: CastRef | null | undefined): CastRef | null {
  return member == null ? null : withoutBlankUsage(member);
}

function homesOf(draft: Draft): Map<string, string> {
  return new Map(
    draft.sections.flatMap((section) =>
      section.requirements.map((ref) => [requirementId(ref), section.key] as const),
    ),
  );
}

type Persona = SectionPlanData['personas'][number];
type SectionCastMember = DraftSection['cast'][number];

function sectionIsIncomplete(section: DraftSection): boolean {
  const count = (part: CastPart) => section.cast.filter((member) => member.part === part).length;

  return (
    blank(section.title) ||
    section.requirements.length === 0 ||
    count('worked-example') !== 1 ||
    count('case-study') !== 1 ||
    blank(section.workedExampleStory) ||
    blank(section.caseStudyStory)
  );
}

function RequiredMark() {
  return (
    <Text as="span" color="red.400" ml={0.5}>
      *
    </Text>
  );
}

function reviewBlockers(
  plan: SectionPlanData,
  draft: Draft,
  homes: Map<string, string>,
  inWorkplace: boolean,
): string[] {
  const homeless = plan.requirements
    .filter((requirement) => !homes.has(requirementId(requirement)))
    .map(requirementLabel);
  const incomplete = draft.sections.flatMap((section, index) =>
    sectionIsIncomplete(section) ? [index + 1] : [],
  );
  const unassignedRoles = draft.recurringCast.guide == null ? ['guide'] : [];
  const missingWorkplace = inWorkplace ? missingWorkplaceFields(draft.learnerProfile) : [];

  const blockers: string[] = [];

  if (homeless.length > 0) {
    blockers.push(t`These requirements have no section: ${homeless.join(', ')}.`);
  }

  if (incomplete.length > 0) {
    blockers.push(t`Sections with required fields to fill in: ${incomplete.join(', ')}.`);
  }

  if (unassignedRoles.length > 0) {
    blockers.push(t`Recurring roles still unassigned: ${unassignedRoles.join(', ')}.`);
  }

  if (missingWorkplace.length > 0) {
    blockers.push(t`Set in a workplace needs: ${missingWorkplace.join(', ')}.`);
  }

  return blockers;
}

function toPayload(
  runId: string,
  plan: SectionPlanData,
  draft: Draft,
  inWorkplace: boolean,
): SaveInput | null {
  const guide = submittableMember(draft.recurringCast.guide);

  if (guide == null || (inWorkplace && missingWorkplaceFields(draft.learnerProfile).length > 0)) {
    return null;
  }

  return {
    id: runId,
    etag: plan.etag,
    sections: draft.sections.map((section) => ({
      key: section.key,
      title: section.title,
      description: section.description,
      requirements: section.requirements,
      foundationSkills: section.foundationSkills,
      cast: section.cast.map(withoutBlankUsage),
      workedExampleStory: section.workedExampleStory,
      caseStudyStory: section.caseStudyStory,
    })),
    recurringCast: {
      guide,
      peer: submittableMember(draft.recurringCast.peer),
      supervisor: hasFixedWorkplace(draft.learnerProfile)
        ? submittableMember(draft.recurringCast.supervisor)
        : null,
    },
    learnerProfile: draft.learnerProfile,
  };
}

type StoryPart = Extract<CastPart, 'worked-example' | 'case-study'>;

function personaLabel(persona: Persona) {
  return persona.roleTitle == null ? persona.name : `${persona.name} (${persona.roleTitle})`;
}

function isSupporting({ part }: SectionCastMember): boolean {
  return part !== 'worked-example' && part !== 'case-study';
}

function optionsWith(personas: Persona[], member: SectionCastMember | undefined): Persona[] {
  return member == null || personas.some(({ id }) => id === member.personaId)
    ? personas
    : [
        { id: member.personaId, name: member.name, roleTitle: undefined, avatarUrl: null },
        ...personas,
      ];
}

function withStoryPerson(
  cast: SectionCastMember[],
  part: StoryPart,
  persona: Persona,
): SectionCastMember[] {
  const holder = cast.find((member) => member.part === part);
  const promoted: SectionCastMember = { personaId: persona.id, name: persona.name, part };
  const others = cast.filter((member) => member.personaId !== persona.id || member.part === part);

  return holder == null
    ? [promoted, ...others]
    : others.map((member) => (member === holder ? promoted : member));
}

function StoryPersonSelect({
  holder,
  part,
  cast,
  personas,
  label,
  onSelect,
}: {
  holder: SectionCastMember | undefined;
  part: StoryPart;
  cast: SectionCastMember[];
  personas: Persona[];
  label: string;
  onSelect: (persona: Persona) => void;
}) {
  const otherStoryPerson = cast.find(
    (member) => member.part != null && member.part !== part && member.part !== 'assessment-only',
  );

  return (
    <Select
      size="sm"
      w={pxToRem(240)}
      value={holder?.personaId ?? ''}
      aria-label={label}
      onChange={(event) => {
        const persona = personas.find(({ id }) => id === event.target.value);

        if (persona != null && persona.id !== holder?.personaId) {
          onSelect(persona);
        }
      }}
    >
      {holder == null ? <option value="">{t`Choose…`}</option> : null}
      {optionsWith(personas, holder)
        .filter(({ id }) => id !== otherStoryPerson?.personaId)
        .map((persona) => (
          <option key={persona.id} value={persona.id}>
            {personaLabel(persona)}
          </option>
        ))}
    </Select>
  );
}

const PILL_CIRCLE = pxToRem(32);
const HEADER_ROW = pxToRem(32);

function PersonaPill({
  name,
  role,
  avatarUrl,
  removeLabel,
  onRemove,
}: {
  name: string;
  role: string;
  avatarUrl: string | null | undefined;
  removeLabel: string;
  onRemove: () => void;
}) {
  return (
    <HStack
      spacing={2}
      p={1}
      borderWidth="1px"
      borderRadius="full"
      bg="bg.muted"
      data-testid={testIds.removeCastButton}
    >
      <Avatar size="sm" boxSize={PILL_CIRCLE} name={name} src={avatarUrl ?? undefined} />
      <Box lineHeight="1.1">
        <Text fontSize="sm" fontWeight="medium">
          {name}
        </Text>

        <Text fontSize="xs" color="fg.muted">
          {role}
        </Text>
      </Box>
      <IconButton
        size="sm"
        boxSize={PILL_CIRCLE}
        minW={PILL_CIRCLE}
        variant="ghost"
        borderRadius="full"
        aria-label={removeLabel}
        icon={<X size={iconSize.sm} />}
        onClick={onRemove}
      />
    </HStack>
  );
}

function SupportingCharacters({
  cast,
  personas,
  onChange,
}: {
  cast: SectionCastMember[];
  personas: Persona[];
  onChange: (cast: SectionCastMember[]) => void;
}) {
  const supporting = cast.filter(isSupporting);
  const pillsRef = useRef<HTMLDivElement>(null);
  const shownCount = useRef(supporting.length);

  useEffect(() => {
    if (supporting.length > shownCount.current) {
      pillsRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
    shownCount.current = supporting.length;
  }, [supporting.length]);

  const choices = [
    ...supporting.flatMap((member) => {
      const persona = personas.find(({ id }) => id === member.personaId);
      return persona == null ? [] : [{ persona, chosen: true }];
    }),
    ...personas
      .filter(({ id }) => !cast.some(({ personaId }) => personaId === id))
      .map((persona) => ({ persona, chosen: false })),
  ];

  const add = (persona: Persona) =>
    onChange([...cast, { personaId: persona.id, name: persona.name, part: null }]);
  const remove = (persona: Persona) =>
    onChange(cast.filter((member) => !(member.personaId === persona.id && isSupporting(member))));

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={2}>
        <GroupHeading>
          <Trans>Supporting characters</Trans>
        </GroupHeading>

        <Popover placement="bottom-end" isLazy>
          <PopoverTrigger>
            <Button
              type="button"
              size="xs"
              variant="outline"
              leftIcon={<Plus size={iconSize.xs} />}
              data-testid={testIds.addCastButton}
            >
              <Trans>Add supporting characters</Trans>
            </Button>
          </PopoverTrigger>

          <PopoverContent w="auto" minW={pxToRem(240)}>
            <PopoverBody maxH={pxToRem(360)} overflowY="auto">
              <VStack align="stretch" spacing={0}>
                {choices.map(({ persona, chosen }) => (
                  <Checkbox
                    key={persona.id}
                    isChecked={chosen}
                    px={2}
                    py={1.5}
                    borderRadius="md"
                    _hover={{ bg: 'bg.muted' }}
                    data-testid={testIds.supportingOption}
                    onChange={() => (chosen ? remove(persona) : add(persona))}
                  >
                    <HStack spacing={2}>
                      <Avatar size="xs" name={persona.name} src={persona.avatarUrl ?? undefined} />
                      <Box lineHeight="1.1">
                        <Text fontSize="sm">{persona.name}</Text>
                        {persona.roleTitle ? (
                          <Text fontSize="xs" color="fg.muted">
                            {persona.roleTitle}
                          </Text>
                        ) : null}
                      </Box>
                    </HStack>
                  </Checkbox>
                ))}
              </VStack>
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </Flex>

      {supporting.length === 0 ? (
        <Text fontSize="sm" color="fg.muted">
          <Trans>
            No supporting characters. Add anyone else who appears in this section's story.
          </Trans>
        </Text>
      ) : null}

      <Flex ref={pillsRef} gap={2} wrap="wrap" align="center" pb={2}>
        {supporting.map((member) => {
          const persona = personas.find(({ id }) => id === member.personaId);
          const role = persona?.roleTitle ?? '';

          return (
            <PersonaPill
              key={member.personaId}
              name={member.name}
              role={member.part === 'assessment-only' ? t`${role} · assessment only` : role}
              avatarUrl={persona?.avatarUrl}
              removeLabel={t`Remove ${member.name}`}
              onRemove={() => onChange(cast.filter((current) => current !== member))}
            />
          );
        })}
      </Flex>
    </Box>
  );
}

function SectionCard({
  section,
  number,
  plan,
  byCode,
  homes,
  ownerTags,
  castPersonas,
  onPatch,
  onMoveRequirement,
  onDelete,
}: {
  castPersonas: Persona[];
  onDelete: (() => void) | null;
  section: DraftSection;
  number: number;
  plan: SectionPlanData;
  byCode: Map<string, SectionPlanData['requirements'][number]>;
  homes: Map<string, string>;
  ownerTags: Map<string, string>;
  onPatch: (patch: Partial<DraftSection>) => void;
  onMoveRequirement: (requirement: RequirementRef, toKey: string | null) => void;
}) {
  const homeOf = (requirement: RequirementRef) => homes.get(requirementId(requirement));
  const workedExample = section.cast.find(({ part }) => part === 'worked-example');
  const caseStudy = section.cast.find(({ part }) => part === 'case-study');

  const requirementGroups = [
    {
      key: 'here',
      label: <Trans>In this section</Trans>,
      rows: plan.requirements.filter((requirement) => homeOf(requirement) === section.key),
      greyed: false,
      linked: true,
    },
    {
      key: 'available',
      label: <Trans>Available</Trans>,
      rows: plan.requirements.filter((requirement) => homeOf(requirement) == null),
      greyed: false,
      linked: false,
    },
    {
      key: 'elsewhere',
      label: <Trans>Allocated elsewhere</Trans>,
      rows: plan.requirements.filter((requirement) => {
        const home = homeOf(requirement);
        return home != null && home !== section.key;
      }),
      greyed: true,
      linked: false,
    },
  ];

  return (
    <VStack align="stretch" spacing={5} data-testid={testIds.sectionCard}>
      <Box>
        <Flex justify="space-between" align="center" mb={2} minH={HEADER_ROW}>
          <GroupHeading>
            <Trans>Section</Trans>
          </GroupHeading>

          {onDelete == null ? null : (
            <Button
              size="xs"
              variant="outline"
              colorScheme="red"
              leftIcon={<Trash2 size={iconSize.xs} />}
              data-testid={testIds.deleteSectionButton}
              onClick={onDelete}
            >
              <Trans>Delete section</Trans>
            </Button>
          )}
        </Flex>

        <FieldLabel>
          <Trans>Title</Trans>
          <RequiredMark />
        </FieldLabel>

        <HStack spacing={2} align="center">
          <Badge colorScheme="gray" fontSize="sm" minW={pxToRem(28)} textAlign="center">
            {number}
          </Badge>
          <Input
            size="sm"
            fontWeight="medium"
            value={section.title}
            placeholder={t`Section title`}
            aria-label={t`Title for section ${number}`}
            onChange={(event) => onPatch({ title: event.target.value })}
          />
        </HStack>

        <Box mt={3}>
          <FieldLabel>
            <Trans>Learning outcome</Trans>
          </FieldLabel>
          <Textarea
            size="sm"
            rows={3}
            value={section.description}
            placeholder={t`What the learner can do after this section`}
            aria-label={t`Learning outcome for section ${number}`}
            onChange={(event) => onPatch({ description: event.target.value })}
          />
        </Box>
      </Box>

      <Divider />

      <Box>
        <Flex justify="space-between" align="center" mb={2}>
          <GroupHeading>
            <Trans>Requirements</Trans>
            <RequiredMark />
          </GroupHeading>

          <RequirementPicker
            groups={requirementGroups}
            triggerLabel={<Trans>Allocate requirements</Trans>}
            placement="bottom-end"
            triggerIcon={Plus}
            triggerProps={{ 'data-testid': testIds.allocateRequirementsButton }}
            optionTestId={testIds.requirementOption}
            idOf={requirementId}
            colourOf={({ kind }) => KIND_COLOURS[kind]}
            holderTagOf={(requirement) => {
              const home = homeOf(requirement);
              return home != null && home !== section.key ? ownerTags.get(home) : undefined;
            }}
            onToggle={(requirement, linked) =>
              onMoveRequirement(requirement, linked ? section.key : null)
            }
          />
        </Flex>

        <Flex gap={2} wrap="wrap" align="center">
          {section.requirements.map((requirement) => (
            <RequirementChip
              key={requirementId(requirement)}
              colourScheme={KIND_COLOURS[requirement.kind]}
              label={requirement.code}
              description={byCode.get(requirementId(requirement))?.description ?? ''}
              removeLabel={t`Unallocate ${requirement.code}`}
              onRemove={() => onMoveRequirement(requirement, null)}
              data-testid={testIds.requirementChip}
            />
          ))}
        </Flex>

        {plan.foundationSkillOptions.length === 0 ? null : (
          <Box mt={3}>
            <FieldLabel>
              <UnitRequirementKindHeading kind="FOUNDATION_SKILL" />
            </FieldLabel>

            <FoundationSkillPicker
              options={plan.foundationSkillOptions}
              selected={section.foundationSkills}
              onToggle={(category, next) =>
                onPatch({
                  foundationSkills: next
                    ? [...section.foundationSkills, category]
                    : section.foundationSkills.filter((skill) => skill !== category),
                })
              }
            />
          </Box>
        )}
      </Box>

      <Divider />

      <Box>
        <Box mb={2}>
          <GroupHeading>
            <Trans>Story</Trans>
          </GroupHeading>
        </Box>

        <Box mb={2}>
          <FieldLabel>
            <Trans>Worked example</Trans>
            <RequiredMark />
          </FieldLabel>
          <StoryPersonSelect
            holder={workedExample}
            part="worked-example"
            cast={section.cast}
            personas={castPersonas}
            label={t`Worked example person in section ${number}`}
            onSelect={(persona) =>
              onPatch({
                cast: withStoryPerson(section.cast, 'worked-example', persona),
              })
            }
          />
        </Box>
        <Textarea
          size="sm"
          rows={5}
          value={section.workedExampleStory}
          aria-label={t`Worked example story for section ${number}`}
          data-testid={testIds.workedExampleStory}
          onChange={(event) => onPatch({ workedExampleStory: event.target.value })}
        />

        <Box mt={3}>
          <Box mb={2}>
            <FieldLabel>
              <Trans>Case study</Trans>
              <RequiredMark />
            </FieldLabel>
            <StoryPersonSelect
              holder={caseStudy}
              part="case-study"
              cast={section.cast}
              personas={castPersonas}
              label={t`Case study person in section ${number}`}
              onSelect={(persona) =>
                onPatch({
                  cast: withStoryPerson(section.cast, 'case-study', persona),
                })
              }
            />
          </Box>
          <Textarea
            size="sm"
            rows={3}
            value={section.caseStudyStory}
            aria-label={t`Case study story for section ${number}`}
            data-testid={testIds.caseStudyStory}
            onChange={(event) => onPatch({ caseStudyStory: event.target.value })}
          />
        </Box>
      </Box>

      <Divider />

      <Box>
        <SupportingCharacters
          cast={section.cast}
          personas={castPersonas}
          onChange={(cast) => onPatch({ cast })}
        />
      </Box>
    </VStack>
  );
}

function CastAndContextCard({
  recurringCast,
  learnerProfile,
  personas,
  inWorkplace,
  onToggleWorkplace,
  onPatch,
}: {
  recurringCast: Draft['recurringCast'];
  learnerProfile: Draft['learnerProfile'];
  personas: Persona[];
  inWorkplace: boolean;
  onToggleWorkplace: (next: boolean) => void;
  onPatch: (patch: Partial<Pick<Draft, 'recurringCast' | 'learnerProfile'>>) => void;
}) {
  const setRecurring = (role: (typeof RECURRING_ROLES)[number], personaId: string) => {
    const persona = personas.find(({ id }) => id === personaId);

    if (persona === undefined && role === 'guide') {
      return;
    }

    onPatch({
      recurringCast: {
        ...recurringCast,
        [role]: persona ? { personaId: persona.id, name: persona.name, part: null } : null,
      },
    });
  };

  return (
    <VStack align="stretch" spacing={5} flex={1} minH={0}>
      <Box>
        <Flex align="center" mb={2} minH={HEADER_ROW}>
          <GroupHeading>
            <Trans>Recurring cast</Trans>
          </GroupHeading>
        </Flex>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          {RECURRING_ROLES.filter((role) => role !== 'supervisor').map((role) => (
            <FormControl key={role}>
              <FormLabel fontSize="xs" textTransform="capitalize" mt={0} mb={1}>
                {role}
              </FormLabel>
              <Select
                size="sm"
                value={recurringCast[role]?.personaId ?? ''}
                aria-label={t`Recurring ${role}`}
                onChange={(event) => setRecurring(role, event.target.value)}
              >
                {role === 'peer' ? <option value="">{t`No recurring peer`}</option> : null}

                {personas.map((persona) => (
                  <option key={persona.id} value={persona.id}>
                    {personaLabel(persona)}
                  </option>
                ))}
              </Select>
            </FormControl>
          ))}
        </SimpleGrid>
      </Box>

      <Divider />

      <Box flex={1} minH={0} display="flex" flexDirection="column">
        <HStack mb={2} justify="space-between" align="center">
          <GroupHeading>
            <Trans>Workplace</Trans>
          </GroupHeading>
          <HStack as="label" spacing={2} align="center" cursor="pointer">
            <Text fontSize="xs" fontWeight="semibold" color="fg.muted" lineHeight="1">
              <Trans>Set in a workplace</Trans>
            </Text>
            <Switch
              size="sm"
              isChecked={inWorkplace}
              data-testid={testIds.workplaceSwitch}
              onChange={(event) => onToggleWorkplace(event.target.checked)}
            />
          </HStack>
        </HStack>

        {inWorkplace ? (
          <SimpleGrid
            columns={{ base: 1, md: 2 }}
            spacing={3}
            flex={1}
            minH={0}
            templateRows={{ md: 'auto auto minmax(0, 1fr)' }}
          >
            <FormControl>
              <FormLabel fontSize="xs" mt={0} mb={1}>
                <Trans>Supervisor</Trans>
              </FormLabel>
              <Select
                size="sm"
                value={recurringCast.supervisor?.personaId ?? ''}
                aria-label={t`Recurring supervisor`}
                onChange={(event) => setRecurring('supervisor', event.target.value)}
              >
                <option value="">{t`No supervisor`}</option>
                {personas.map((persona) => (
                  <option key={persona.id} value={persona.id}>
                    {personaLabel(persona)}
                  </option>
                ))}
              </Select>
            </FormControl>

            {LEARNER_PROFILE_FIELDS.map(({ key, label, long, required }) => {
              const value = learnerProfile[key];
              const shared = {
                size: 'sm' as const,
                value: typeof value === 'string' ? value : '',
                'aria-label': label,
              };
              const patchField = (next: string) =>
                onPatch({ learnerProfile: { ...learnerProfile, [key]: next } });

              return (
                <FormControl
                  key={key}
                  isRequired={required}
                  gridColumn={long ? { md: 'span 2' } : undefined}
                  display={long ? 'flex' : undefined}
                  flexDirection="column"
                  minH={0}
                >
                  <FormLabel fontSize="xs" mt={0} mb={1}>
                    {label}
                  </FormLabel>

                  {long ? (
                    <Textarea
                      {...shared}
                      flex={1}
                      minH={pxToRem(120)}
                      resize="none"
                      onChange={(event) => patchField(event.target.value)}
                    />
                  ) : (
                    <Input {...shared} onChange={(event) => patchField(event.target.value)} />
                  )}
                </FormControl>
              );
            })}
          </SimpleGrid>
        ) : null}
      </Box>
    </VStack>
  );
}

export function AllocationReviewModal({
  runId,
  isOpen,
  onClose,
}: {
  runId: string;
  isOpen: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const utils = trpcAdmin.useUtils();
  const [edits, setEdits] = useState<Draft | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const [deleteKey, setDeleteKey] = useState<string | null>(null);
  const [selected, setSelected] = useState<PaneSelection>({ kind: 'castAndContext' });
  const [workplaceOverride, setWorkplaceOverride] = useState<boolean | null>(null);
  const [clearedWorkplace, setClearedWorkplace] = useState<Pick<
    Draft,
    'learnerProfile' | 'recurringCast'
  > | null>(null);

  const { data: plan, error } = trpcAdmin.unitsOfCompetency.sectionPlan.useQuery(
    { id: runId },
    { enabled: isOpen, refetchOnWindowFocus: false, staleTime: Infinity },
  );

  const isDirty = edits != null;

  const saveMutation = trpcAdmin.unitsOfCompetency.saveAndContinueFromAllocation.useMutation({
    onSuccess: () => {
      toast({ status: 'success', title: t`Generating assessments` });
      void utils.unitsOfCompetency.listGenerations.invalidate();
      void utils.unitsOfCompetency.generationById.invalidate({ id: runId });
      onClose();
    },
    onError: (mutationError) => {
      if (mutationError.data?.code === 'CONFLICT') {
        void utils.unitsOfCompetency.sectionPlan.invalidate({ id: runId });
      }

      toast({
        status: 'error',
        title: t`Could not continue`,
        description: mutationError.message,
      });
    },
  });

  const requestClose = () => {
    if (isDirty) {
      setConfirmClose(true);
      return;
    }

    onClose();
  };

  if (plan == null) {
    return (
      <ReviewShell isOpen={isOpen} onClose={requestClose} title={<Trans>Review allocation</Trans>}>
        <PendingBody error={error} />
      </ReviewShell>
    );
  }

  const draft: Draft = edits ?? plan;
  const { unitCode } = plan;

  const requirementsByCode = new Map(
    plan.requirements.map((requirement) => [requirementId(requirement), requirement]),
  );

  const homes = homesOf(draft);
  const recurringIds = new Set(
    RECURRING_ROLES.flatMap((role) => {
      const member = draft.recurringCast[role];
      return member == null ? [] : [member.personaId];
    }),
  );
  const castPersonas = plan.personas.filter(({ id }) => !recurringIds.has(id));
  const sectionTags = new Map(
    draft.sections.map((section, index) => [section.key, `S${index + 1}`] as const),
  );
  const activeSectionIndex =
    selected.kind === 'section' ? draft.sections.findIndex(({ key }) => key === selected.key) : -1;

  const activeSection = activeSectionIndex === -1 ? undefined : draft.sections[activeSectionIndex];
  const inWorkplace = workplaceOverride ?? hasFixedWorkplace(draft.learnerProfile);
  const blockers = reviewBlockers(plan, draft, homes, inWorkplace);
  const canContinue = blockers.length === 0;
  const onSave = () => {
    const payload = toPayload(runId, plan, draft, inWorkplace);

    if (payload != null) {
      saveMutation.mutate(payload);
    }
  };

  const patchDraft = (patch: Partial<Draft>) => setEdits({ ...draft, ...patch });

  const toggleWorkplace = (next: boolean) => {
    setWorkplaceOverride(next);

    if (next) {
      if (clearedWorkplace != null) {
        patchDraft(clearedWorkplace);
        setClearedWorkplace(null);
      }
      return;
    }

    setClearedWorkplace({
      learnerProfile: draft.learnerProfile,
      recurringCast: draft.recurringCast,
    });
    patchDraft({
      learnerProfile: Object.fromEntries(LEARNER_PROFILE_FIELDS.map(({ key }) => [key, ''])),
      recurringCast: { ...draft.recurringCast, supervisor: null },
    });
  };

  const addSection = () => {
    const key = crypto.randomUUID();

    setEdits({
      ...draft,
      sections: [
        ...draft.sections,
        {
          key,
          title: '',
          description: '',
          requirements: [],
          foundationSkills: [],
          cast: [],
          workedExampleStory: '',
          caseStudyStory: '',
        },
      ],
    });

    setSelected({ kind: 'section', key });
  };

  const deleteSection = (key: string) => {
    const index = draft.sections.findIndex((section) => section.key === key);
    const previous = draft.sections[index - 1];

    setEdits({ ...draft, sections: draft.sections.filter((section) => section.key !== key) });
    setSelected(previous ? { kind: 'section', key: previous.key } : { kind: 'castAndContext' });
  };

  const patchActiveSection = (patch: Partial<DraftSection>) =>
    setEdits({
      ...draft,
      sections: draft.sections.map((current, at) =>
        at === activeSectionIndex ? { ...current, ...patch } : current,
      ),
    });

  const moveRequirement = (requirement: RequirementRef, toKey: string | null) => {
    const id = requirementId(requirement);

    setEdits({
      ...draft,
      sections: draft.sections.map((section) => {
        const without = section.requirements.filter((ref) => requirementId(ref) !== id);

        return section.key === toKey
          ? { ...section, requirements: [...without, requirement] }
          : { ...section, requirements: without };
      }),
    });
  };

  return (
    <ReviewShell
      isOpen={isOpen}
      onClose={requestClose}
      title={<Trans>Review allocation</Trans>}
      subtitle={<Trans>Sections, cast and requirement allocation for {unitCode}.</Trans>}
    >
      <ModalBody display="flex" flexDirection="column" overflow="hidden">
        <ReviewLayout
          paneKey={selected.kind === 'section' ? selected.key : selected.kind}
          outline={
            <>
              <OutlineItem
                isSelected={selected.kind === 'castAndContext'}
                hasIssue={
                  draft.recurringCast.guide == null ||
                  (inWorkplace && missingWorkplaceFields(draft.learnerProfile).length > 0)
                }
                label={<Trans>Recurring cast & workplace</Trans>}
                onSelect={() => setSelected({ kind: 'castAndContext' })}
              />

              <Divider my={2} />

              <OutlineHeading>
                <Trans>Sections</Trans>
              </OutlineHeading>

              {draft.sections.map((section, index) => (
                <OutlineItem
                  key={section.key}
                  isSelected={selected.kind === 'section' && selected.key === section.key}
                  hasIssue={sectionIsIncomplete(section)}
                  badge={
                    <Badge
                      colorScheme="gray"
                      fontSize="xs"
                      flexShrink={0}
                      minW={pxToRem(24)}
                      textAlign="center"
                    >
                      {index + 1}
                    </Badge>
                  }
                  label={blank(section.title) ? t`Untitled section` : section.title}
                  onSelect={() => setSelected({ kind: 'section', key: section.key })}
                />
              ))}

              <Button
                variant="secondary"
                w="full"
                mt={2}
                leftIcon={<Plus size={iconSize.md} />}
                data-testid={testIds.addSectionButton}
                onClick={addSection}
              >
                <Trans>Add section</Trans>
              </Button>
            </>
          }
        >
          {activeSection != null ? (
            <SectionCard
              key={activeSection.key}
              section={activeSection}
              number={activeSectionIndex + 1}
              plan={plan}
              byCode={requirementsByCode}
              homes={homes}
              ownerTags={sectionTags}
              castPersonas={castPersonas}
              onPatch={patchActiveSection}
              onMoveRequirement={moveRequirement}
              onDelete={draft.sections.length > 1 ? () => setDeleteKey(activeSection.key) : null}
            />
          ) : (
            <CastAndContextCard
              recurringCast={draft.recurringCast}
              learnerProfile={draft.learnerProfile}
              personas={plan.personas}
              inWorkplace={inWorkplace}
              onToggleWorkplace={toggleWorkplace}
              onPatch={patchDraft}
            />
          )}
        </ReviewLayout>
      </ModalBody>

      <ModalFooter gap={2}>
        {blockers.length > 0 ? (
          <BlockersAlert status="error" messages={blockers} data-testid={testIds.coverageWarning} />
        ) : null}

        <Button variant="secondary" onClick={requestClose}>
          <Trans>Close</Trans>
        </Button>

        <Button
          colorScheme="brand"
          isDisabled={!canContinue}
          isLoading={saveMutation.isPending}
          data-testid={testIds.saveAndContinueButton}
          onClick={onSave}
        >
          {plan.isRetry ? <Trans>Retry assessments</Trans> : <Trans>Generate assessments</Trans>}
        </Button>
      </ModalFooter>

      <ConfirmationDialog
        isOpen={deleteKey != null}
        onClose={() => setDeleteKey(null)}
        title={<Trans>Delete this section?</Trans>}
        body={
          <Text>
            <Trans>
              Its requirements go back to unallocated, ready to move to another section.
            </Trans>
          </Text>
        }
        onConfirm={() => {
          if (deleteKey != null) {
            deleteSection(deleteKey);
          }
          setDeleteKey(null);
        }}
        confirmLabel={<Trans>Delete section</Trans>}
        cancelLabel={<Trans>Keep it</Trans>}
        variant="destructive"
      />

      <ConfirmationDialog
        isOpen={confirmClose}
        onClose={() => setConfirmClose(false)}
        title={<Trans>Discard your changes?</Trans>}
        body={
          <Text>
            <Trans>This allocation has unsaved edits. Closing loses them.</Trans>
          </Text>
        }
        onConfirm={() => {
          setConfirmClose(false);
          onClose();
        }}
        confirmLabel={<Trans>Discard</Trans>}
        cancelLabel={<Trans>Keep editing</Trans>}
        variant="destructive"
      />
    </ReviewShell>
  );
}
